import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Search, X, ChevronDown } from 'lucide-react';

export interface ClassTrialFiltersState {
  searchQuery: string;
  statusFilter: string[];
  classTypeFilter: string[];
  locationFilter: string[];
  gradeFilter: string[];
}

interface ClassTrialFiltersProps {
  filters: ClassTrialFiltersState;
  onFiltersChange: (filters: ClassTrialFiltersState) => void;
  statusOptions: string[];
  classTypeOptions: string[];
  locationOptions: string[];
  gradeOptions: string[];
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerLabel = selected.length === 0
    ? 'ทั้งหมด'
    : `${selected.length} รายการ`;

  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between text-sm font-normal h-10"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2 max-h-[300px] overflow-y-auto" align="start">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
            >
              <Checkbox
                checked={selected.includes(option)}
                onCheckedChange={() => toggleValue(option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function ClassTrialFilters({
  filters,
  onFiltersChange,
  statusOptions,
  classTypeOptions,
  locationOptions,
  gradeOptions,
}: ClassTrialFiltersProps) {
  const updateFilter = (key: keyof ClassTrialFiltersState, value: string[] | string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const removeFilterValue = (key: keyof ClassTrialFiltersState, value: string) => {
    const current = filters[key] as string[];
    onFiltersChange({ ...filters, [key]: current.filter((v) => v !== value) });
  };

  const clearFilters = () => {
    onFiltersChange({
      searchQuery: '',
      statusFilter: [],
      classTypeFilter: [],
      locationFilter: [],
      gradeFilter: [],
    });
  };

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.statusFilter.length > 0 ||
    filters.classTypeFilter.length > 0 ||
    filters.locationFilter.length > 0 ||
    filters.gradeFilter.length > 0;

  const allBadges: { key: keyof ClassTrialFiltersState; label: string; value: string }[] = [];
  for (const v of filters.statusFilter) allBadges.push({ key: 'statusFilter', label: v, value: v });
  for (const v of filters.classTypeFilter) allBadges.push({ key: 'classTypeFilter', label: v, value: v });
  for (const v of filters.locationFilter) allBadges.push({ key: 'locationFilter', label: v, value: v });
  for (const v of filters.gradeFilter) allBadges.push({ key: 'gradeFilter', label: v, value: v });

  return (
    <div className="bg-card border rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Search */}
        <div className="relative lg:col-span-2 space-y-1">
          <span className="text-xs font-medium text-muted-foreground">ค้นหา</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ชื่อ, ชื่อเล่น, รหัสครู..."
              value={filters.searchQuery}
              onChange={(e) => updateFilter('searchQuery', e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <MultiSelectFilter
          label="สถานะ"
          options={statusOptions}
          selected={filters.statusFilter}
          onChange={(v) => updateFilter('statusFilter', v)}
        />
        <MultiSelectFilter
          label="ประเภท"
          options={classTypeOptions}
          selected={filters.classTypeFilter}
          onChange={(v) => updateFilter('classTypeFilter', v)}
        />
        <MultiSelectFilter
          label="สถานที่"
          options={locationOptions}
          selected={filters.locationFilter}
          onChange={(v) => updateFilter('locationFilter', v)}
        />
        <MultiSelectFilter
          label="ชั้นเรียน"
          options={gradeOptions}
          selected={filters.gradeFilter}
          onChange={(v) => updateFilter('gradeFilter', v)}
        />
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {allBadges.map((badge) => (
            <Badge
              key={`${badge.key}-${badge.value}`}
              variant="secondary"
              className="flex items-center gap-1 text-xs"
            >
              {badge.label}
              <button
                onClick={() => removeFilterValue(badge.key, badge.value)}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground h-6 text-xs">
            <X className="h-3 w-3 mr-1" />
            ล้างทั้งหมด
          </Button>
        </div>
      )}
    </div>
  );
}
